import { ITelemetryItem } from '@microsoft/applicationinsights-core-js';
import { appInsights } from 'src/shared/lib/appInsights';
import { eventObservers } from 'src/lib/amplitude';
import { formatLocalUrlTemplate } from 'src/lib/utils/stringUtils';
import { getJoinToken, getAthenaContextId } from 'src/components/utils/Helper';
import english from 'src/i18n/en';
import spanish from 'src/i18n/es';

// initialize Application Insights SDK
const initAppInsights = () => {
  const correlationHeaderDomains = [process.env.BACKEND_BASE_URL].map((url) => {
    if (process.env.ENVIRONMENT === 'local') {
      url = formatLocalUrlTemplate(url as string);
    }
    return new URL(url as string).hostname;
  });

  // Determine app insights environment.  If 'production' then translate to 'prod' as this is
  // used in the name of the px-prod-ai app insights production instance
  const aiEnv = (process.env.ENVIRONMENT || 'dev').toLowerCase().replace('production', 'prod');

  appInsights.init({
    key: String(process.env.APPINSIGHTS_KEY),
    config: { enableCorsCorrelation: true, correlationHeaderDomains },
    shouldDelayTelemetry: () => !appInsights.customTelemetryTags['ai.operation.name'],
    callback: () => {
      // do not track any dependency telemetry associated with amplitude or zoom
      appInsights.addIgnoredTarget('amplitude.com');
      appInsights.addIgnoredTarget('zoom.us');

      appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
        // add window.location.pathname to all appinsights telemetry
        envelope.data = { ...envelope.data, route: window.location.pathname };
      });

      // add build time and package.json version to customDimensions
      appInsights.customTelemetryData.thBuildTimestamp = thBuildInfo.timestamp;
      appInsights.customTelemetryData.thBuildVersion = thBuildInfo.version;
      appInsights.customTelemetryData.app = 'th-patient';

      // add joinToken/contextId to customDimensions if user is in meeting workflow
      const joinToken = getJoinToken();
      if (joinToken) {
        // casting to string here places contextId in customDimensions instead of customMeasurements
        appInsights.customTelemetryData.contextId = String(getAthenaContextId());
        appInsights.customTelemetryData.joinToken = joinToken;
      }

      // Note: Currently both patient/provider apps use the same roleInstance value and contain the word "patient".
      // In the future we may want to tease them appart and set the provider app to use provider-telehealth-env, which
      // will in turn require updating the existing telehealth AI alerts to include both role instances or create new
      // alerts to track this separate roleInstance variation
      appInsights.customTelemetryTags['ai.cloud.roleInstance'] = `patient-telehealth-${aiEnv}`;
      // save the first known operation id and re-use for all subsequent telemetry
      if (!sessionStorage.getItem('ai.operation.id')) {
        sessionStorage.setItem('ai.operation.id', appInsights.getTraceCtx()?.getTraceId() as string);
      }
      appInsights.customTelemetryTags['ai.operation.id'] = sessionStorage.getItem('ai.operation.id');

      // these errors are very noisey but appear to be benign and affect very few appointments.
      // only send 1 occurence to App Insights for each appointment session
      appInsights.addThrottledException('ErrorEvent: Script error');
      appInsights.addThrottledException('ErrorEvent: ResizeObserver loop limit exceeded');
    },
  });

  eventObservers.addObserver((event, data) => {
    if (event.match(/PageView/)) {
      // derive pageName from data object
      appInsights.trackPageView({ name: data['Common::PageName'] });
    }
  });
};

const initMetaTag = () => {
  if (window.location.pathname !== '/') {
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');
  }
};

const initDevConsole = (ldUserKey: string) => {
  if (String(process.env.ENVIRONMENT || '') !== 'production') {
    import(
      /* webpackChunkName: "dev-console-bootstrap" */
      /* webpackPrefetch: true */
      'src/shared/components/DevConsole/init'
    ).then(({ init }): void => {
      init({
        ldAnonymous: true,
        ldClientSideId: process.env.LAUNCH_DARKLY_CLIENT_ID as string,
        ldContextId: getAthenaContextId(),
        ldUserKey,
        translations: { english, spanish },
      });
    });
  }
};

export const initApp = (ldUserKey: string) => {
  try {
    initAppInsights();
    initMetaTag();
    initDevConsole(ldUserKey);
  } catch (error) {
    console.error((error as Error).stack);
  }
};
